import { useEffect, useRef, useState } from "react";
import {
  getCachedEntry,
  isCurrentGeneration,
  notifyFailure,
  notifySubscribers,
  serializeQueryKey,
  setCachedEntry,
  startFetch,
  subscribeInvalidate,
  type QueryKey,
} from "./queryClient";

export interface UseQueryOptions<T> {
  /** Resource + normalized params/record ID only — never tokens, PII, or `client_id` (design.md section 7). */
  key: QueryKey;
  queryFn: (signal: AbortSignal) => Promise<T>;
  /** Default `true`. Set `false` to defer fetching until `refetch()` is called explicitly. */
  enabled?: boolean;
  /** When set, polls at this interval while `enabled`, the document is visible, and the browser is online. */
  pollIntervalMs?: number;
  /** Stops polling once this returns `true` for the latest data (e.g. a terminal campaign lifecycle state). */
  isTerminal?: (data: T | undefined) => boolean;
}

export interface UseQueryResult<T> {
  data: T | undefined;
  error: unknown;
  /** `true` only while the very first request for this key is pending (no data to show yet). */
  loading: boolean;
  /** `true` while a request is pending AND stale/previous data is already available to show underneath it. */
  refreshing: boolean;
  /** `true` once a refresh has failed — the displayed `data` is no longer known-current. */
  stale: boolean;
  updatedAt: number | undefined;
  /**
   * Codex finding (PR #387 review round 3, TemplateDetailScreen): the
   * underlying `runFetch(true)` this always calls already resolves a
   * `boolean` (`true` success / `false` failure — it never rejects on a
   * normal fetch failure), but this type previously declared `() => void`,
   * hiding that from every caller and forcing a bare try/catch that could
   * never actually observe a failed refetch. Every existing call site that
   * ignores the return value (the overwhelming majority) is unaffected —
   * discarding a Promise is always valid.
   */
  refetch: () => Promise<boolean>;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

interface QueryState<T> {
  data: T | undefined;
  updatedAt: number | undefined;
  error: unknown;
  stale: boolean;
  phase: "idle" | "loading" | "success" | "error";
}

/** Small typed GET hook around `apiFetch` (design.md section 7): dedup, abort/latest-wins, stale-after-failed-refresh labeling, visibility/offline-paused polling with terminal stop, exact-key mutation refetch — no new runtime dependency (task 2.5 gate). */
export function useQuery<T>(options: UseQueryOptions<T>): UseQueryResult<T> {
  const keyStr = JSON.stringify(options.key);
  const enabled = options.enabled ?? true;

  // Only keyStr/enabled/pollIntervalMs drive effect re-runs; queryFn/isTerminal read via this ref.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [state, setState] = useState<QueryState<T>>(() => {
    const cached = getCachedEntry<T>(options.key);
    return cached
      ? { data: cached.data, updatedAt: cached.updatedAt, error: undefined, stale: false, phase: "success" }
      : { data: undefined, updatedAt: undefined, error: undefined, stale: false, phase: "idle" };
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // `enabled` gates only automatic fetching; explicit refetch() always runs.
  // Latest-wins is tracked per KEY in queryClient (isCurrentGeneration), not
  // per hook instance — a per-instance ref cannot see a newer fetch started
  // by a DIFFERENT useQuery instance watching the same key (review fix P1).
  // Returns whether the settled fetch counts as a poll SUCCESS (true) or
  // FAILURE (false) — used only by the polling effect's backoff below. A
  // superseded/aborted/stale-key response counts as success so a response
  // this instance no longer cares about never drives backoff up.
  const runFetch = (force: boolean): Promise<boolean> => {
    const key = optionsRef.current.key; // captured once, used for the whole lifecycle of this fetch (review fix P2)
    setState((prev) => ({ ...prev, phase: "loading" }));
    const { promise, generation } = startFetch<T>(key, optionsRef.current.queryFn, { force });
    return promise.then(
      (data) => {
        if (!isCurrentGeneration(key, generation)) return true; // superseded by a newer fetch (any instance) — latest response wins
        setCachedEntry(key, data); // still valid shared-cache data for `key`, useful to any other instance still watching it
        // Codex finding (panel-redesign-v3 PR #381): broadcast the fresh data
        // to every OTHER instance watching this key too — one of them may be
        // the instance THIS fetch's own `force:true` start just aborted (see
        // `notifySubscribers`'s doc comment), and without this it would stay
        // stuck in its loading phase forever instead of adopting this result.
        notifySubscribers(key);
        // review fix round 2, bug 2: `key`'s OWN generation bucket says nothing
        // about whether THIS instance has since moved to a different key — a
        // key change doesn't touch the old key's bucket, so a stale response
        // for it can still pass isCurrentGeneration. Re-read the current key
        // fresh (not the `key` captured at fetch-start) before touching local state.
        if (serializeQueryKey(optionsRef.current.key) !== serializeQueryKey(key)) return true;
        setState({ data, updatedAt: Date.now(), error: undefined, stale: false, phase: "success" });
        return true;
      },
      (error: unknown) => {
        if (!isCurrentGeneration(key, generation)) return true;
        if (isAbortError(error)) return true;
        // Codex finding (round 4, PR #381): a superseded subscriber whose
        // own in-flight request this one aborted was never told when the
        // WINNING request settled with a FAILURE instead of a success,
        // leaving it stuck in its loading phase ("Actualizando...")
        // forever. `notifyFailure` (distinct from the success-only
        // `notifySubscribers`) tells every subscriber's listener below to
        // settle out of its loading phase directly, WITHOUT retrying —
        // nothing new is cached on a failure, so retrying automatically
        // here would cascade into an extra fetch for every subscriber,
        // including this one's own self-subscription.
        notifyFailure(key, error);
        if (serializeQueryKey(optionsRef.current.key) !== serializeQueryKey(key)) return true;
        setState((prev) => ({ ...prev, error, phase: "error", stale: prev.data !== undefined }));
        return false;
      },
    );
  };

  useEffect(() => {
    if (enabled) {
      runFetch(false);
    }
    // Codex finding (P2, revision automatica sobre PR #285): `invalidateExact`
    // notifies every subscriber of this key SYNCHRONOUSLY, in one loop
    // (queryClient.ts `notify`). `force:true` here would make EACH
    // subscriber's own `startFetch` abort-and-supersede the PREVIOUS
    // subscriber's own in-flight fetch (same key, force always bumps the
    // shared generation) -- only the LAST notified subscriber's fetch would
    // ever resolve into its own state; every earlier one would fail its
    // `isCurrentGeneration` check and stay stuck (stale data, or stuck
    // `phase: "loading"` forever). `force:false` lets every subscriber but
    // the first dedupe onto that ONE shared in-flight fetch instead
    // (startFetch's own `if (existing && !options.force) return existing`)
    // -- one network call, every subscriber's state updates from the same
    // resolution. Nothing is in-flight yet on a genuinely fresh invalidate
    // (no concurrent fetch), so `force:false` still starts a real fetch —
    // it only changes behavior when something for this key is ALREADY
    // in-flight, exactly the multi-subscriber case this fixes.
    return subscribeInvalidate(optionsRef.current.key, (reason, broadcastError) => {
      // Codex finding (round 4, PR #381): a FAILURE broadcast means the
      // winning request for this key settled with no new data at all —
      // calling `runFetch(false)` here (the `"data"` branch below) would
      // dedupe onto nothing (the failed fetch already cleared itself from
      // `inflight`) and start a genuinely NEW, unrequested fetch for every
      // subscriber, including the failing instance's own self-subscription.
      // Just settle this instance out of its loading phase instead, with no
      // retry.
      if (reason === "failure") {
        if (stateRef.current.phase === "loading") {
          // Codex finding (round 6, PR #381): a subscriber with no cached
          // data needs an explicit `error` value, not just the `phase` flip
          // — screens such as OverviewScreen only render their retry panel
          // when `error !== undefined`, so leaving it `undefined` left the
          // page blank instead of showing a retry affordance.
          setState((prev) => ({
            ...prev,
            error: prev.error ?? broadcastError,
            phase: "error",
            stale: prev.data !== undefined,
          }));
        }
        return;
      }
      // A plain `runFetch(false)` here was only ever exercised by an actual
      // invalidation (cache already cleared before notifying, so there is
      // nothing to adopt and a real fetch is correct). `notifySubscribers`
      // now ALSO fires this same listener right after a different
      // instance's successful fetch already wrote fresh data for this key
      // — in that case the cache already holds a newer result than this
      // instance has ever shown, so adopt it directly instead of issuing a
      // redundant network request (or, for a genuinely superseded/aborted
      // instance, instead of staying stuck in its loading phase with
      // nothing to resolve it).
      const cached = getCachedEntry<T>(optionsRef.current.key);
      if (cached && (stateRef.current.updatedAt === undefined || cached.updatedAt > stateRef.current.updatedAt)) {
        setState({ data: cached.data, updatedAt: cached.updatedAt, error: undefined, stale: false, phase: "success" });
        return;
      }
      runFetch(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyStr/enabled are the only reactive deps by design; queryFn/key read via optionsRef
  }, [keyStr, enabled]);

  // redesign-panel-hubspot-mailgun, PR 16 slice D (task 16.5, design.md line
  // 265 "backs off after failure"): a bare fixed-interval `setInterval`
  // hammered a down backend every `pollIntervalMs` regardless of the
  // previous attempt's outcome. Self-rescheduling via `setTimeout` lets the
  // NEXT delay depend on whether the poll that just settled succeeded —
  // doubled per consecutive failure, capped at 8x base, reset to base on
  // the next success. A hidden/offline tick still reschedules at the base
  // interval (unrelated to backoff — it isn't a failed request, just a
  // skipped one) and does not count as a failure.
  useEffect(() => {
    const pollIntervalMs = optionsRef.current.pollIntervalMs;
    if (!pollIntervalMs || !enabled) return undefined;
    const MAX_BACKOFF_STEPS = 3; // 1x, 2x, 4x, 8x base — then holds at 8x
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let consecutiveFailures = 0;

    const scheduleNext = (delayMs: number): void => {
      if (cancelled) return;
      timeoutId = setTimeout(tick, delayMs);
    };

    function tick(): void {
      if (cancelled) return;
      if (document.hidden || !navigator.onLine) {
        scheduleNext(pollIntervalMs!);
        return;
      }
      if (optionsRef.current.isTerminal?.(stateRef.current.data)) {
        return; // terminal: stop polling entirely, no further reschedule
      }
      runFetch(true).then((succeeded) => {
        consecutiveFailures = succeeded ? 0 : Math.min(consecutiveFailures + 1, MAX_BACKOFF_STEPS);
        scheduleNext(pollIntervalMs! * 2 ** consecutiveFailures);
      });
    }

    scheduleNext(pollIntervalMs);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyStr/enabled/pollIntervalMs are the only reactive deps by design
  }, [keyStr, enabled, options.pollIntervalMs]);

  return {
    data: state.data,
    error: state.error,
    loading: state.phase === "loading" && state.data === undefined,
    refreshing: state.phase === "loading" && state.data !== undefined,
    stale: state.stale,
    updatedAt: state.updatedAt,
    refetch: () => runFetch(true),
  };
}
